import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ArrowLeft, CreditCard, Tag, Check, Sparkles } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Initialize Stripe (will be loaded from backend config)
let stripePromise = null;

const CheckoutForm = ({ amount, credits, onSuccess, selectedPackage }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [discountApplied, setDiscountApplied] = useState(null);
  const [finalAmount, setFinalAmount] = useState(amount);
  const [finalCredits, setFinalCredits] = useState(credits);

  const token = localStorage.getItem('token');

  const validateDiscount = async () => {
    if (!discountCode.trim()) {
      toast.error('Please enter a discount code');
      return;
    }

    setValidatingDiscount(true);
    try {
      const response = await axios.post(
        `${API}/payments/validate-discount`,
        { code: discountCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const discount = response.data;
      const discountAmount = (amount * discount.discount_percentage) / 100;
      const newAmount = amount - discountAmount;
      const newCredits = Math.floor(newAmount * 15); // 1 GBP = 15 credits

      setDiscountApplied(discount);
      setFinalAmount(newAmount);
      setFinalCredits(newCredits);
      toast.success(`${discount.discount_percentage}% discount applied!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid discount code');
      setDiscountApplied(null);
      setFinalAmount(amount);
      setFinalCredits(credits);
    } finally {
      setValidatingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setDiscountCode('');
    setDiscountApplied(null);
    setFinalAmount(amount);
    setFinalCredits(credits);
    toast.success('Discount removed');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      // Create payment intent
      const { data } = await axios.post(
        `${API}/payments/create-payment-intent`,
        {
          amount: finalAmount,
          discount_code: discountApplied ? discountCode : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Confirm payment
      const result = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: {
          card: elements.getElement(CardElement)
        }
      });

      if (result.error) {
        toast.error(result.error.message);
      } else {
        // Payment successful
        const confirmResponse = await axios.post(
          `${API}/payments/confirm-payment`,
          { payment_intent_id: result.paymentIntent.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success(confirmResponse.data.message);
        onSuccess(confirmResponse.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Discount Code Section */}
      <Card className="border-purple-200 bg-purple-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-3">
            <Tag className="w-5 h-5 text-purple-600" />
            <label className="font-medium text-purple-900">Have a Discount Code?</label>
          </div>
          
          {!discountApplied ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                placeholder="Enter discount code"
                className="flex-1 px-4 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 uppercase"
                disabled={validatingDiscount}
              />
              <Button
                type="button"
                onClick={validateDiscount}
                disabled={validatingDiscount || !discountCode.trim()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {validatingDiscount ? 'Validating...' : 'Apply'}
              </Button>
            </div>
          ) : (
            <div className="bg-green-100 border border-green-300 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-green-900 font-medium">
                  {discountApplied.discount_percentage}% discount applied!
                </span>
              </div>
              <Button
                type="button"
                onClick={removeDiscount}
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                Remove
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Order Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-gray-700">
            <span>{selectedPackage || 'Custom Amount'}</span>
            <span>£{amount.toFixed(2)}</span>
          </div>
          {discountApplied && (
            <div className="flex justify-between text-green-600">
              <span>Discount ({discountApplied.discount_percentage}%)</span>
              <span>-£{(amount - finalAmount).toFixed(2)}</span>
            </div>
          )}
          <div className="border-t pt-3 flex justify-between font-bold text-lg">
            <span>Total</span>
            <span>£{finalAmount.toFixed(2)}</span>
          </div>
          <div className="bg-indigo-50 rounded-lg p-3 text-center">
            <span className="text-indigo-900">
              You will receive <strong className="text-2xl">{finalCredits}</strong> credits
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Card Details */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 border border-gray-300 rounded-lg">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Your payment is secure and encrypted. We never store your card details.
          </p>
        </CardContent>
      </Card>

      <Button
        type="submit"
        disabled={!stripe || processing}
        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-lg py-6"
      >
        {processing ? 'Processing...' : `Pay £${finalAmount.toFixed(2)}`}
      </Button>
    </form>
  );
};

export default function PurchaseCredits() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [currentCredits, setCurrentCredits] = useState(0);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchConfig();
    fetchCurrentCredits();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/payments/config`);
      setConfig(response.data);
      
      // Initialize Stripe with publishable key (you'll need to add this to backend config endpoint)
      const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (stripeKey && stripeKey !== 'pk_live_YOUR_KEY_HERE') {
        stripePromise = loadStripe(stripeKey);
      }
    } catch (error) {
      console.error('Failed to fetch payment config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentCredits = async () => {
    try {
      const response = await axios.get(`${API}/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentCredits(response.data.credits || 0);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setCustomAmount('');
    setShowCheckout(true);
  };

  const handleCustomAmount = () => {
    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount < 1) {
      toast.error('Minimum purchase amount is £1');
      return;
    }
    setSelectedPackage({
      amount: amount,
      credits: Math.floor(amount * (config?.credits_per_pound || 15)),
      label: 'Custom Amount'
    });
    setShowCheckout(true);
  };

  const handleSuccess = (data) => {
    setShowCheckout(false);
    setSelectedPackage(null);
    setCustomAmount('');
    setCurrentCredits(data.new_balance);
    
    // Navigate back to credits page after a short delay
    setTimeout(() => {
      navigate('/credits');
    }, 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/credits')}
            variant="outline"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Credits
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Purchase Credits</h1>
              <p className="text-gray-600 mt-2">Choose a package or enter a custom amount</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Current Balance</p>
              <p className="text-3xl font-bold text-indigo-600">{currentCredits} Credits</p>
            </div>
          </div>
        </div>

        {!showCheckout ? (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Preset Packages */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold mb-6">Preset Packages</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {config?.preset_packages?.map((pkg, index) => (
                  <Card
                    key={index}
                    className={`cursor-pointer transition-all hover:shadow-lg hover:scale-105 ${
                      pkg.badge ? 'border-2 border-indigo-500' : ''
                    }`}
                    onClick={() => handlePackageSelect(pkg)}
                  >
                    {pkg.badge && (
                      <div className="bg-indigo-500 text-white text-sm font-bold py-1 px-4 rounded-t-lg text-center">
                        {pkg.badge}
                      </div>
                    )}
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.label}</h3>
                        <div className="text-4xl font-bold text-indigo-600 mb-1">
                          £{pkg.amount}
                        </div>
                        <div className="text-2xl font-semibold text-gray-700 mb-4">
                          {pkg.credits} Credits
                        </div>
                        <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                          <CreditCard className="w-4 h-4 mr-2" />
                          Purchase
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Custom Amount */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Custom Amount</h2>
              <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                    <h3 className="text-xl font-bold text-purple-900">Choose Your Amount</h3>
                  </div>
                  <p className="text-sm text-purple-700 mb-4">
                    Enter any amount from £1. You'll get 15 credits per pound.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Amount (£)
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder="Enter amount"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-lg"
                      />
                    </div>
                    {customAmount && parseFloat(customAmount) >= 1 && (
                      <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                        <p className="text-sm text-gray-600">You'll receive</p>
                        <p className="text-3xl font-bold text-purple-600">
                          {Math.floor(parseFloat(customAmount) * 15)} Credits
                        </p>
                      </div>
                    )}
                    <Button
                      onClick={handleCustomAmount}
                      disabled={!customAmount || parseFloat(customAmount) < 1}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      Continue to Payment
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Info Card */}
              <Card className="mt-4 bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <h4 className="font-bold text-blue-900 mb-2">💡 How Credits Work</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• 1 pound (£) = 15 credits</li>
                    <li>• Generate CV/Cover Letter: 5 credits</li>
                    <li>• Credits never expire</li>
                    <li>• Secure payment via Stripe</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            <Button
              onClick={() => setShowCheckout(false)}
              variant="outline"
              className="mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Packages
            </Button>

            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <CheckoutForm
                  amount={selectedPackage.amount}
                  credits={selectedPackage.credits}
                  onSuccess={handleSuccess}
                  selectedPackage={selectedPackage.label}
                />
              </Elements>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-red-600 font-medium">
                    Stripe is not configured. Please contact support.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
